<!--
  single-page/10000/Write.vue — AI 안전지수 보조(위험성평가) 작성 컴포넌트.

  공용 boards/tab/Write.vue 가 동적 import(single-page/include.js·require.context) 하는 자식 폼.
  계약: props { isPrint, board, value } · watch.value(hydrate) · watch.form($emit('input')).
  ★ 저장/API(post 저장)는 공용 wrapper 전담 — 본 컴포넌트는 emit('input')만.
  ★ 기본정보는 기존 프로젝트 공용 컴포넌트(field-select-module) 를 사용한다.
     (MIRAS 위험성평가표/위험성평가 정보 입력은 AI 안전지수 집계와 무관해 2026-07-24 제거.)
  ★ AI 분석(2026-07-01 연동): GET /api/v1/project/{projectId}/ai-safety/full-assist 호출.
     백엔드(GetAiSafetyFullAssistController + AiSafetyFactorBuilder)가 SHMS 전반을 site×기간 집계해
     spec 33 factors 를 구성 → sidecar /safety/full-assist. (프론트가 위험성평가 1건만 보내던 이전 방식
     POST /api/v1/miras/safety/full-assist 는 3/33·충실도 9% 문제로 폐기.) JWT 는 HttpRequest 자동 부착.
     응답의 cto_factor_diagnostics(N/33)로 충실도 표시. safety_diagnostics(위험조합·경보) 는 있으면 노출.
  ★ 점수 단독 표시 금지(근거·limitations 동반) · 사고예측/확률 아님 · 사후변수 입력 없음 · 자동 승인 없음.
-->
<template>
  <div class="ai-safety-assist">
    <!-- 1) 기본정보 -->
    <field-select-module v-model="form.stdInfo" :board="board" :is-print="isPrint" :readonly="isReadonly" :options="[{ type: 'alert', label: '알림 대상자 선택' }]" />

    <!-- 2) AI 안전지수 보완 입력 — 사업장(프로젝트) 단위. SHMS 보드에 소스가 없는 프로파일 인자를
         project.meta.ai_safety_inputs 에 저장해 33/33 집계에 반영한다. -->
    <div class="my-4" v-if="!isPrint">
      <div>
        <h5>
          <i class="dot"></i> {{ $t("AI 안전지수 보완 입력") }}
          <span class="meta-note">{{ $t("(사업장 단위 · 보드에 없는 항목만 직접 입력)") }}</span>
        </h5>
      </div>
    </div>
    <div v-if="!isPrint" class="meta-panel">
      <p class="help-text">{{ $t("아래 값은 사업장(프로젝트) 전체에 적용됩니다. [AI 분석 실행]을 누르면 자동 저장되어 안전지수 집계에 반영됩니다(별도 [보완 입력 저장]도 가능). 모르는 값은 비워 두세요(0을 넣지 마세요).") }}</p>
      <div class="writeGridContainer">
        <div v-for="def in metaFactorDefs" :key="def.key">
          <div class="meta-label">
            {{ $t(def.label) }}<span v-if="def.unit" class="meta-unit">({{ def.unit }})</span>
          </div>
          <vs-select v-if="def.type === 'cmr'" class="w-full" v-model="metaFactors[def.key]" :disabled="isReadonly || metaLoading">
            <vs-select-item value="" :text="$t('미입력')" />
            <vs-select-item :value="1" :text="$t('취급함')" />
            <vs-select-item :value="0" :text="$t('취급 안 함')" />
          </vs-select>
          <ml-input v-else type="number" class="w-full" v-model="metaFactors[def.key]" :readonly="isReadonly || metaLoading" :placeholder="$t('미입력')" />
        </div>
      </div>
      <div class="meta-actions" v-if="!isReadonly">
        <vs-button color="primary" type="border" :disabled="metaSaving || metaLoading" @click="saveMetaInputs">
          {{ metaSaving ? $t("저장 중…") : $t("보완 입력 저장") }}
        </vs-button>
        <span v-if="metaSavedMsg" class="meta-saved">{{ metaSavedMsg }}</span>
      </div>
    </div>

    <!-- 3) AI 안전지수 분석 실행(작성 단계만) -->
    <div class="my-4">
      <div>
        <h5><i class="dot"></i> {{ $t("AI 안전지수 보조") }}</h5>
      </div>
    </div>
    <div class="ai-action" v-if="!isReadonly">
      <vs-button color="primary" :disabled="aiLoading" @click="runAiAnalysis">
        {{ aiLoading ? $t("AI 분석 중…") : $t("AI 분석 실행") }}
      </vs-button>
      <span v-if="aiError" class="ai-error">{{ $t("분석 실패") }}: {{ aiError }} ({{ $t("다시 시도") }})</span>
    </div>
    <div class="ai-action" v-else>
      <em>{{ $t("결재 진행 중에는 AI 결과를 조회·참고만 합니다(문서 수정 불가).") }}</em>
    </div>

    <!-- 4) AI 결과 패널(상시 노출·점수 단독 금지) -->
    <div class="ai-result mt-4" v-if="hasResult">
      <!-- 상단 요약: 반원 게이지 + 핵심 지표 -->
      <div class="result-head">
        <div class="gauge-wrap">
          <svg viewBox="0 0 220 120" class="gauge-svg">
            <path v-for="(a, i) in gauge.arcs" :key="'arc' + i" :d="a.d" :stroke="a.color" stroke-width="16" fill="none" stroke-linecap="round" />
            <!-- 바늘: 색상 밴드/텍스트와 구분되도록 어두운 색 고정 -->
            <line :x1="gauge.cx" :y1="gauge.cy" :x2="gauge.needle.x" :y2="gauge.needle.y" stroke="#3b3a47" stroke-width="3.5" stroke-linecap="round" />
            <circle :cx="gauge.cx" :cy="gauge.cy" r="7" fill="#fff" stroke="#3b3a47" stroke-width="2.5" />
          </svg>
          <!-- 점수/등급은 게이지 아래로 분리(바늘·중심축과 겹치지 않게) -->
          <div class="gauge-readout">
            <span class="gauge-score" :style="{ color: gradeInfo.color }">{{ result.safety_index }}</span>
            <span class="gauge-grade" :style="{ color: gradeInfo.color }">{{ gradeInfo.label }}</span>
          </div>
          <div class="gauge-caption">{{ $t("안전지수") }} · 0~100 · {{ $t("높을수록 안전") }}</div>
        </div>

        <div class="metric-grid">
          <div class="metric">
            <div class="metric-label">
              {{ $t("입력 충실도") }}
              <vx-tooltip :title="$t('입력 충실도란?')" :html="$t('안전지수 33개 항목 중 SHMS 운영 데이터로 집계된 비율입니다.<br>낮을수록 AI 결과의 신뢰도가 떨어져 데이터 보완이 필요합니다.')" position="top">
                <feather-icon icon="HelpCircleIcon" class="help-ic" />
              </vx-tooltip>
            </div>
            <div class="metric-value" :class="{ 'val-warn': isLowCompleteness }">
              <template v-if="factorFill">{{ factorFill.count }}/33 <span class="metric-unit">({{ pct(factorFill.ratio) }})</span></template>
              <template v-else>{{ pct(result.input_completeness) }}</template>
            </div>
            <div v-if="isLowCompleteness" class="metric-sub warn-text">{{ $t("데이터가 부족합니다") }}</div>
          </div>
          <div class="metric">
            <div class="metric-label">
              {{ $t("검토 상태") }}
              <vx-tooltip :title="$t('검토 상태란?')" :html="$t('AI 결과에 대한 사람의 검토·결재 단계입니다.<br>작성 중 → 검토 대기 → 승인 또는 반려.')" position="top">
                <feather-icon icon="HelpCircleIcon" class="help-ic" />
              </vx-tooltip>
            </div>
            <div class="metric-value">{{ hitlLabel(result.hitl_status) }}</div>
          </div>
          <div class="metric">
            <div class="metric-label">{{ $t("승인 여부") }}</div>
            <div class="metric-value" :class="{ 'val-warn': result.approval_required }">
              {{ result.approval_required ? $t("결재 승인 필요") : $t("추가 승인 불필요") }}
            </div>
          </div>
        </div>
      </div>

      <!-- 정보 보완 안내 -->
      <div v-if="result.missing_critical_inputs && result.missing_critical_inputs.length" class="info-box warn">
        <feather-icon icon="AlertTriangleIcon" class="ib-ic" />
        <div>
          <b>{{ $t("정보 보완이 필요합니다.") }}</b>
          <div class="ib-sub">
            {{ $t("다음 항목을 추가로 입력하면 결과가 더 정확해집니다.") }}
            <span v-for="(m, i) in result.missing_critical_inputs" :key="'m' + i" class="chip">{{ friendlyMissing(m) }}</span>
          </div>
        </div>
      </div>

      <!-- 위험 조합·조기경보(진단 신호 — 점수 미반영). sidecar early_warning 연동 시 노출 -->
      <section v-if="safetyDiagnostics" class="rs-section">
        <h6 class="rs-title">
          {{ $t("위험 조합·조기경보") }}
          <span class="rs-note">{{ $t("(진단·예방 지원 신호 — 안전지수 점수에는 반영되지 않습니다)") }}</span>
        </h6>

        <!-- 경보 티어 -->
        <div v-if="safetyDiagnostics.alert_tier" class="alert-tier" :class="alertTierClass(safetyDiagnostics.alert_tier.code)">
          <feather-icon icon="AlertOctagonIcon" class="at-ic" />
          <div>
            <div class="at-headline">{{ safetyDiagnostics.alert_tier.headline || safetyDiagnostics.alert_tier.tier }}</div>
            <div v-if="safetyDiagnostics.alert_tier.guidance" class="at-guide">{{ safetyDiagnostics.alert_tier.guidance }}</div>
          </div>
        </div>

        <!-- 위험 조합 -->
        <div v-if="safetyDiagnostics.cross_term && safetyDiagnostics.cross_term.active_count" class="diag-block">
          <div class="diag-title">{{ $t("동시에 겹친 위험 조합") }} ({{ safetyDiagnostics.cross_term.active_count }})</div>
          <ul class="diag-list">
            <li v-for="(t, i) in safetyDiagnostics.cross_term.active_terms" :key="'ct' + i">
              {{ t.combination || t.raw }}
              <span v-if="t.weight" class="diag-weight">×{{ t.weight }}</span>
            </li>
          </ul>
          <p v-if="safetyDiagnostics.cross_term.meaning" class="help-text">{{ safetyDiagnostics.cross_term.meaning }}</p>
        </div>

        <!-- 조기경보 -->
        <div v-if="safetyDiagnostics.early_warning && safetyDiagnostics.early_warning.length" class="diag-block">
          <div class="diag-title">{{ $t("조기경보") }}</div>
          <ul class="ew-list">
            <li v-for="(w, i) in safetyDiagnostics.early_warning" :key="'ew' + i" class="ew-item">
              <span class="ew-level" :class="ewLevelClass(w.level)">{{ w.level }}</span>
              <span class="ew-msg">{{ w.message }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- 안전지수에 영향을 준 요인(구 "왜 위험한가") -->
      <section class="rs-section">
        <h6 class="rs-title">{{ $t("안전지수에 영향을 준 요인") }}</h6>
        <p class="help-text">{{ $t("아래 항목이 현재 안전지수에 영향을 주고 있습니다. 막대가 길수록 영향이 큽니다. (안전지수 계산 근거일 뿐, 사고를 예측하지 않습니다.)") }}</p>
        <ul class="cause-list" v-if="result.risk_causes && result.risk_causes.length">
          <li v-for="(c, i) in result.risk_causes" :key="'c' + i">
            <div class="cause-row">
              <span class="cause-desc">{{ c.description || c.factor }}</span>
              <span class="cause-pct">{{ contributionPct(c.contribution) }}</span>
            </div>
            <div class="cause-bar"><div class="cause-bar-fill" :style="{ width: contributionWidth(c.contribution) }"></div></div>
            <span class="cause-code">{{ $t("코드") }} {{ c.factor }}</span>
          </li>
        </ul>
        <p v-else class="help-text">{{ $t("표시할 요인이 없습니다.") }}</p>
      </section>

      <!-- 개선조치 추천 + 채택/수정/반려 (unified_recommendations 우선) -->
      <section class="rs-section">
        <h6 class="rs-title">
          {{ $t("개선조치 추천") }}
          <span class="rs-note">{{ $t("(사람이 검토 후 채택·수정·반려 — 자동 반영되지 않습니다)") }}</span>
        </h6>
        <div class="overflow-x-auto">
        <table class="rec-table">
          <thead>
            <tr>
              <th>{{ $t("추천") }}</th>
              <th>{{ $t("구분") }}</th>
              <th>{{ $t("상태") }}</th>
              <th>{{ $t("근거") }}</th>
              <th>{{ $t("결정") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="recommendations.length === 0">
              <td colspan="5" class="empty-cell">{{ $t("추천된 개선조치가 없습니다.") }}</td>
            </tr>
            <tr v-for="rec in recommendations" :key="rec.key" :class="{ 'rec-row-rejected': decisionRaw(rec.key) === 'reject' }">
              <td>
                <div class="rec-title">{{ rec.title }}</div>
                <div v-if="rec.action" class="rec-action">{{ rec.action }}</div>
              </td>
              <td>{{ rec.target }}</td>
              <td>{{ rec.status }}</td>
              <td>
                <ul v-if="rec.evidence.length" class="evidence-list">
                  <li v-for="(ev, ei) in rec.evidence" :key="rec.key + '-ev' + ei">
                    {{ ev.citation_label || ev.title || ev.code }}
                  </li>
                </ul>
                <span v-else>{{ $t("근거 없음") }}</span>
              </td>
              <td class="decision-cell">
                <template v-if="!isReadonly">
                  <div class="rec-actions">
                    <vs-button size="small" color="success" :type="decisionRaw(rec.key) === 'accept' ? 'filled' : 'border'" @click="acceptRec(rec.key)">{{ $t("채택") }}</vs-button>
                    <vs-button size="small" type="border" class="ml-1" @click="startEdit(rec.key)">{{ $t("수정") }}</vs-button>
                    <vs-button size="small" color="danger" :type="decisionRaw(rec.key) === 'reject' ? 'filled' : 'border'" class="ml-1" @click="rejectRec(rec.key)">{{ $t("반려") }}</vs-button>
                  </div>

                  <!-- 인라인 편집창 -->
                  <div v-if="editingKey === rec.key" class="edit-box">
                    <ml-textarea v-model="editText" rows="3" class="m-0" />
                    <div class="edit-actions mt-2">
                      <vs-button size="small" color="primary" @click="saveEdit(rec.key)">{{ $t("저장") }}</vs-button>
                      <vs-button size="small" type="border" class="ml-1" @click="cancelEdit">{{ $t("취소") }}</vs-button>
                    </div>
                  </div>
                </template>
                <span v-if="decisionRaw(rec.key)" class="decision-badge" :class="'badge-' + decisionRaw(rec.key)">{{ decisionLabel(rec.key) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </section>

      <!-- 유의사항(한계) — 상시 노출. 평문 안내 + 기술 정보 분리(접기 없음) -->
      <div class="limitations">
        <div class="lim-title"><feather-icon icon="InfoIcon" class="lim-ic" /> {{ $t("이 결과를 볼 때 유의사항") }}</div>
        <ul class="lim-list">
          <li v-for="(l, i) in plainLimitations" :key="'pl' + i">{{ l }}</li>
        </ul>
        <div v-if="techLimitations.length" class="lim-tech">
          <div class="lim-tech-title">{{ $t("기술 정보") }}</div>
          <ul class="lim-tech-list">
            <li v-for="(l, i) in techLimitations" :key="'tl' + i">{{ l }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 5) 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="isReadonly"></file-upload-module>
  </div>
</template>

<script>
import HttpRequest from "@/util/HttpRequest";
import FieldSelectModule from "@/views/modules/FieldSelectModule.vue";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";

function emptyForm() {
  return {
    stdInfo: {},
    ai_safety_input: {}, // full-assist 요청 payload
    ai_safety_result: {}, // sidecar 결과
    ai_review: {}, // { [index]: { decision, edited_text } }
    ai_audit: {}, // { audit_log_id, sidecar_called_at }
    fileList: [],
  };
}

export default {
  name: "AiSafetyAssistWrite10000",
  components: {
    FieldSelectModule,
    FileUploadModule,
  },
  props: {
    isPrint: { type: Boolean, default: false },
    board: { type: Object, default: () => ({}) },
    value: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: emptyForm(),
      aiLoading: false,
      aiError: "",
      editingKey: null, // 인라인 편집 중인 추천 key
      editText: "", // 편집 버퍼
      // 보완 입력(project.meta.ai_safety_inputs.factors) — 보드 소스 없는 프로파일 인자.
      metaFactors: {}, // { "SF-050": "90", ... }
      metaLoading: false,
      metaSaving: false,
      metaSavedMsg: "",
      metaFactorDefs: [
        { key: "SF-018", label: "총 근로자 수", unit: "명", type: "number" },
        { key: "SF-026", label: "안전관리자 경력", unit: "년", type: "number" },
        { key: "SF-030", label: "신규입사자 비율", unit: "%", type: "number" },
        { key: "SF-050", label: "설비 정기점검 이행률", unit: "%", type: "number" },
        { key: "SF-053", label: "방호장치 양호율", unit: "%", type: "number" },
        { key: "SF-055", label: "유해물질(CMR) 취급", unit: "", type: "cmr" },
        { key: "SF-066", label: "위험도 개선율", unit: "%", type: "number" },
        { key: "SF-091", label: "3년 사망재해 건수", unit: "건", type: "number" },
        { key: "SF-103", label: "예산 집행률", unit: "%", type: "number" },
      ],
    };
  },
  mounted() {
    if (!this.isPrint) {
      this.loadMetaInputs();
    }
  },
  computed: {
    isReadonly() {
      // 인쇄 모드 또는 결재 진행(공용 board.readonly)일 때 입력/분석 비활성
      return !!this.isPrint || !!(this.board && this.board.readonly);
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    result() {
      return this.form.ai_safety_result || {};
    },
    hasResult() {
      return !!(this.result && this.result.safety_index !== undefined);
    },
    recommendations() {
      // 응답은 unified_recommendations 에 추천을 담는다. (recommended_actions 는 비어있는 경우가 많음)
      const uni = this.result.unified_recommendations;
      let rows = [];
      if (uni && uni.length) {
        rows = uni.map((r, i) => ({
          key: r.recommendation_id || `uni${i}`,
          title: r.title || "",
          action: r.action_text || "",
          target: this.targetLabel(r.workflow_target),
          status: this.sectionLabel(r.section) || (r.rag_basis && r.rag_basis.verdict) || "",
          evidence: r.evidence_basis || [],
        }));
      } else {
        const acts = this.result.recommended_actions || [];
        rows = acts.map((a, i) => ({
          key: `act${i}`,
          title: a.action || "",
          action: "",
          target: "",
          status: a.status || "",
          evidence: [].concat(a.evidence_basis || [], a.legal_basis || []),
        }));
      }
      // 제목 없는 추천(예: "(제목 없음)")은 표시하지 않는다.
      return rows.filter((r) => r.title && String(r.title).trim());
    },
    safetyIndexNum() {
      const v = Number(this.result.safety_index);
      if (isNaN(v)) return 0;
      if (v < 0) return 0;
      if (v > 100) return 100;
      return v;
    },
    gradeInfo() {
      const s = this.safetyIndexNum;
      if (s < 50) return { label: this.$t("위험"), color: "#ea5455" };
      if (s < 65) return { label: this.$t("주의"), color: "#ff9f43" };
      if (s < 80) return { label: this.$t("보통"), color: "#f6c343" };
      return { label: this.$t("양호"), color: "#28c76f" };
    },
    gauge() {
      const cx = 110;
      const cy = 110;
      const r = 92;
      const bands = [
        { from: 0, to: 50, color: "#ea5455" },
        { from: 50, to: 65, color: "#ff9f43" },
        { from: 65, to: 80, color: "#f6c343" },
        { from: 80, to: 100, color: "#28c76f" },
      ];
      const arcs = bands.map((b) => {
        const p1 = this.polar(cx, cy, r, this.scoreToAngle(b.from));
        const p2 = this.polar(cx, cy, r, this.scoreToAngle(b.to));
        return { d: `M ${p1.x} ${p1.y} A ${r} ${r} 0 0 1 ${p2.x} ${p2.y}`, color: b.color };
      });
      const needle = this.polar(cx, cy, r - 22, this.scoreToAngle(this.safetyIndexNum));
      return { cx, cy, r, arcs, needle };
    },
    isLowCompleteness() {
      // 신규 백엔드 응답의 factor_fill_ratio(N/33) 우선, 없으면 sidecar input_completeness.
      const f = this.factorFill;
      if (f && typeof f.ratio === "number") return f.ratio < 0.6;
      const c = this.result.input_completeness;
      return typeof c === "number" && c < 0.6;
    },
    // 신규 백엔드(GetAiSafetyFullAssistController) 진단: 몇 개 인자가 집계됐는지(N/33).
    factorFill() {
      const d = this.result.cto_factor_diagnostics;
      if (d && typeof d.factor_count === "number") {
        return {
          count: d.factor_count,
          ratio: typeof d.factor_fill_ratio === "number" ? d.factor_fill_ratio : d.factor_count / 33,
          missing: d.missing_factors || [],
        };
      }
      return null;
    },
    // 위험 조합·경보 티어·조기경보(sidecar early_warning 드롭인 연동 시 노출). 없으면 null.
    safetyDiagnostics() {
      const d = this.result.safety_diagnostics;
      if (d && d.available) return d;
      return null;
    },
    causeMax() {
      const list = this.result.risk_causes || [];
      let max = 0;
      for (const c of list) {
        const v = Number(c.contribution);
        if (!isNaN(v) && v > max) max = v;
      }
      return max;
    },
    plainLimitations() {
      return (this.result.limitations || []).filter((l) => !this.isTechnicalLimitation(l));
    },
    techLimitations() {
      return (this.result.limitations || []).filter((l) => this.isTechnicalLimitation(l));
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(v) {
        if (!v || typeof v !== "object") return;
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (v[key] !== undefined) {
            this.form[key] = v[key];
          }
        }
      },
    },
    form: {
      deep: true,
      handler() {
        const v = this.value || {};
        // 목록 title·요약 — 공용 wrapper가 저장 title/summary로 사용
        const std = this.form.stdInfo || {};
        const cycle = std.date || "";
        v.title = [std.field_name, cycle].filter(Boolean).join(" ");
        v.summary = this.buildSummary();
        const keys = Object.keys(this.form);
        for (const key of keys) {
          v[key] = this.form[key];
        }
        // PII가 필요한 경우에만 v.enc 사용(서버 AES256). 본 템플릿은 평문 PII 미수집.
        this.$emit("input", v);
      },
    },
  },
  methods: {
    buildSummary() {
      // 안전지수 분포(빈도 카운트) — 확률/예측 표현 없음
      const si = this.result.safety_index;
      return si === undefined ? {} : { 안전지수: [{ [String(si)]: 1 }] };
    },
    pct(x) {
      return x === null || x === undefined ? "-" : `${Math.round(x * 100)}%`;
    },
    targetLabel(t) {
      if (t === "corrective_action") return this.$t("시정조치");
      if (t === "work_permit") return this.$t("작업허가");
      if (t === "tbm") return this.$t("TBM(작업 전 안전점검회의)");
      if (t === "inspection") return this.$t("안전점검");
      if (t === "education") return this.$t("안전교육");
      return t || "";
    },
    sectionLabel(s) {
      if (s === "ready") return this.$t("바로 적용 가능");
      if (s === "review") return this.$t("검토 필요");
      if (s === "missing_input") return this.$t("정보 보완 필요");
      if (s === "control_gap") return this.$t("대책 보완 필요");
      return s || "";
    },
    // safety_diagnostics 경보 티어/조기경보 등급 → 색상 클래스
    alertTierClass(code) {
      if (code === "D5_IMMEDIATE") return "tier-immediate";
      if (code === "D4_PRIORITY") return "tier-priority";
      return "tier-observe";
    },
    ewLevelClass(level) {
      if (level === "즉시개입") return "lv-immediate";
      if (level === "우선점검") return "lv-priority";
      return "lv-observe";
    },
    // 반원 게이지 계산: 점수(0~100) → 각도(180°=좌, 0°=우) → 좌표
    scoreToAngle(s) {
      let v = Number(s);
      if (isNaN(v)) v = 0;
      if (v < 0) v = 0;
      if (v > 100) v = 100;
      return 180 * (1 - v / 100); // eslint-disable-line no-mixed-operators
    },
    polar(cx, cy, r, angle) {
      const rad = (angle * Math.PI) / 180;
      const x = cx + (r * Math.cos(rad)); // eslint-disable-line no-mixed-operators
      const y = cy - (r * Math.sin(rad)); // eslint-disable-line no-mixed-operators
      return {
        x: Math.round(x * 100) / 100,
        y: Math.round(y * 100) / 100,
      };
    },
    hitlLabel(s) {
      if (s === "draft") return this.$t("작성 중(검토 전)");
      if (s === "pending_review") return this.$t("검토 대기");
      if (s === "approved") return this.$t("승인됨");
      if (s === "rejected") return this.$t("반려됨");
      return s || "-";
    },
    contributionPct(c) {
      const v = Number(c);
      if (isNaN(v)) return "";
      return `${(v * 100).toFixed(1)}%`;
    },
    contributionWidth(c) {
      const v = Number(c);
      const max = this.causeMax;
      if (isNaN(v) || max <= 0) return "0%";
      return `${Math.round((v / max) * 100)}%`;
    },
    friendlyMissing(m) {
      if (typeof m === "string" && m.indexOf("engine_factors") === 0) {
        const match = m.match(/\((\d+)\/(\d+)\)/);
        if (match) return `안전지수 입력 인자 (총 ${match[2]}개 중 ${match[1]}개 입력)`;
        return "안전지수 입력 인자";
      }
      return m;
    },
    // 일반 사용자에게 의미 약한 기술/엔진 메타 한계 문구 판별
    isTechnicalLimitation(l) {
      if (typeof l !== "string") return false;
      return /engine=|sha256|si_scaled|si_calibrated|KP v|candidate|transform_spec|R60/.test(l);
    },
    findRec(key) {
      return this.recommendations.find((r) => r.key === key);
    },
    decisionRaw(key) {
      const r = this.form.ai_review[key];
      return r && r.decision ? r.decision : "";
    },
    decisionLabel(key) {
      const d = this.decisionRaw(key);
      if (d === "accept") return this.$t("채택됨");
      if (d === "edit") return this.$t("수정됨");
      if (d === "reject") return this.$t("반려됨");
      return "";
    },
    // ★ 사람 결정만 기록(HITL) — 위험성평가표 자동 반영 없음(오 이사님 설계: "자동 반영 없음").
    //   채택된 추천의 실제 반영처는 시정조치/작업허가 보드 draft 이며, 그 보드 연동은 운영 결선(F3) 범위.
    setReview(key, decision, editedText) {
      this.$set(this.form.ai_review, key, {
        decision,
        ai_recommendation_ref: key,
        edited_text: editedText === undefined || editedText === null ? "" : editedText,
        decided_at: new Date().toISOString(),
      });
    },
    // 채택 — 결정 기록만
    acceptRec(key) {
      const prev = this.form.ai_review[key] || {};
      this.setReview(key, "accept", prev.edited_text);
    },
    // 반려 — 결정 기록만(추천 행을 흐리게 표시)
    rejectRec(key) {
      const prev = this.form.ai_review[key] || {};
      this.setReview(key, "reject", prev.edited_text);
    },
    // 수정 — 인라인 편집창에서 edited_text 만 기록(표 변경 없음)
    startEdit(key) {
      const rec = this.findRec(key);
      const prev = this.form.ai_review[key] || {};
      this.editingKey = key;
      if (prev.edited_text) {
        this.editText = prev.edited_text;
      } else if (rec) {
        this.editText = rec.action || rec.title || "";
      } else {
        this.editText = "";
      }
    },
    saveEdit(key) {
      this.setReview(key, "edit", this.editText);
      this.editingKey = null;
    },
    cancelEdit() {
      this.editingKey = null;
    },
    // 보완 입력 로드 — project.meta.ai_safety_inputs.factors 를 폼에 채운다.
    async loadMetaInputs() {
      if (!this.projectId) return;
      this.metaLoading = true;
      try {
        const res = await HttpRequest.get(`/api/v1/project/${this.projectId}/ai-safety/inputs`);
        const inputs = (res && res.ai_safety_inputs) || {};
        const factors = inputs.factors || {};
        const next = {};
        for (const def of this.metaFactorDefs) {
          const v = factors[def.key];
          next[def.key] = v === null || v === undefined ? "" : v;
        }
        this.metaFactors = next;
      } catch (e) {
        // 조회 실패는 조용히 무시(빈 폼 유지) — 저장은 별도로 시도 가능.
        this.metaFactors = {};
      } finally {
        this.metaLoading = false;
      }
    },
    // 보완 입력 저장 — 채워진 값만 factors 로 전송(빈 값은 제외 → 백엔드가 key 제거).
    //   silent=true 면 알림 없이 저장만(분석 실행 직전 자동 저장용). 성공 여부를 boolean 으로 반환.
    async saveMetaInputs(silent) {
      if (this.isReadonly || !this.projectId) return false;
      this.metaSaving = true;
      if (!silent) this.metaSavedMsg = "";
      const factors = {};
      const managedKeys = [];
      for (const def of this.metaFactorDefs) {
        managedKeys.push(def.key);
        const v = this.metaFactors[def.key];
        if (v !== "" && v !== null && v !== undefined && !isNaN(Number(v))) {
          factors[def.key] = Number(v);
        }
      }
      try {
        await HttpRequest.post(`/api/v1/project/${this.projectId}/ai-safety/inputs`, { factors, managed_keys: managedKeys });
        if (!silent) {
          this.metaSavedMsg = this.$t("저장되었습니다. [AI 분석 실행] 시 반영됩니다.");
          this.$vs.notify({ title: this.$t("저장 완료"), text: this.$t("보완 입력이 저장되었습니다."), color: "success" });
        }
        return true;
      } catch (e) {
        if (!silent) {
          this.metaSavedMsg = "";
          this.$vs.notify({ title: this.$t("저장 실패"), text: (e && e.message) || "unknown", color: "danger" });
        }
        return false;
      } finally {
        this.metaSaving = false;
      }
    },
    parseAiError(e) {
      // HttpRequest 는 실패 시 axios err 를 reject → err.response.data 에 error_code/detail.
      if (e && e.response && e.response.data) {
        const d = e.response.data;
        if (d.error_code === "E_AI_SAFETY_NO_FACTORS") return this.$t("집계된 안전지수 입력 인자가 없습니다. SHMS 데이터를 입력한 뒤 다시 시도해주세요.");
        if (d.error_code === "E_AI_SAFETY_SIDECAR_CALL_FAILED") return this.$t("안전지수 엔진 호출에 실패했습니다. 잠시 후 다시 시도해주세요.");
        if (typeof d.detail === "string" && d.detail) return d.detail;
      }
      if (e && e.message) return e.message;
      return "unknown";
    },
    async runAiAnalysis() {
      if (this.isReadonly) return;

      this.aiLoading = true;
      this.aiError = "";
      try {
        // ★ 보완 입력(폼)을 먼저 저장 → project.meta 에 반영된 상태로 분석이 돌게 한다.
        //   (보완 입력 폼 값은 full-assist GET 요청 본문이 아니라 project.meta 를 경유해 전달된다.)
        await this.saveMetaInputs(true);
        // ★ 신규 연동(2026-07-01): 프론트가 위험성평가 1건만 보내지 않는다.
        //   백엔드(GetAiSafetyFullAssistController)가 SHMS 전반을 site×기간으로 집계해
        //   spec 33 factors 를 구성한 뒤 sidecar /safety/full-assist 를 호출한다.
        //   HttpRequest 는 응답 본문(response.data)을 그대로 resolve → res 자체가 결과 객체.
        const res = await HttpRequest.get(`/api/v1/project/${this.projectId}/ai-safety/full-assist`);
        this.form.ai_safety_result = res || {};
        this.form.ai_audit = {
          audit_log_id: (res && res.audit_log_id) || "",
          factor_count: res && res.cto_factor_diagnostics ? res.cto_factor_diagnostics.factor_count : null,
          sidecar_called_at: new Date().toISOString(),
        };
      } catch (e) {
        // fail-closed: 결과 미표시·사람 판단 유지
        this.aiError = this.parseAiError(e);
        this.form.ai_safety_result = {};
      } finally {
        this.aiLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ai-action {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ai-error {
  color: #c0392b;
}
.meta-note {
  font-size: 12px;
  font-weight: 400;
  color: #888;
  margin-left: 4px;
}
.meta-panel {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fafafa;
}
.meta-label {
  font-size: 13px;
  color: #5e5873;
  margin-bottom: 4px;
}
.meta-unit {
  color: #b9b8c5;
  font-size: 12px;
  margin-left: 2px;
}
.meta-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}
.meta-saved {
  color: #28c76f;
  font-size: 12.5px;
}
.ai-result {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
}

/* 상단 요약: 게이지 + 지표 */
.result-head {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}
.gauge-wrap {
  position: relative;
  width: 220px;
  flex-shrink: 0;
}
.gauge-svg {
  display: block;
  width: 220px;
  height: 120px;
  margin: 0 auto;
}
.gauge-readout {
  text-align: center;
  line-height: 1.1;
  margin-top: 2px;
}
.gauge-score {
  font-size: 32px;
  font-weight: 800;
}
.gauge-grade {
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-top: 1px;
}
.gauge-caption {
  text-align: center;
  color: #999;
  font-size: 11px;
  margin-top: 4px;
}
.metric-grid {
  display: flex;
  gap: 12px;
  flex: 1;
  flex-wrap: wrap;
}
.metric {
  min-width: 130px;
  flex: 1;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafafa;
}
.metric-label {
  font-size: 12px;
  color: #6e6b7b;
  display: flex;
  align-items: center;
  gap: 4px;
}
.metric-value {
  font-size: 17px;
  font-weight: 700;
  color: #5e5873;
  margin-top: 6px;
}
.metric-value.val-warn {
  color: #ea5455;
}
.metric-sub {
  font-size: 11px;
  margin-top: 2px;
}
.warn-text {
  color: #ea5455;
}
.metric-unit {
  font-size: 12px;
  font-weight: 500;
  color: #6e6b7b;
}

/* 위험 조합·조기경보(safety_diagnostics) */
.alert-tier {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 8px;
  margin: 6px 0 12px;
}
.alert-tier .at-ic {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
}
.at-headline {
  font-weight: 700;
}
.at-guide {
  font-size: 12.5px;
  margin-top: 3px;
}
.tier-immediate {
  background: #fdeaea;
  border: 1px solid #f5b5b5;
  color: #c0392b;
}
.tier-priority {
  background: #fff6e9;
  border: 1px solid #ffd9a8;
  color: #a05a00;
}
.tier-observe {
  background: #fff8e1;
  border: 1px solid #ffe9a8;
  color: #7a6500;
}
.diag-block {
  margin-bottom: 12px;
}
.diag-title {
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 4px;
}
.diag-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #5e5873;
}
.diag-weight {
  color: #b9b8c5;
  font-size: 12px;
}
.ew-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ew-item {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 4px 0;
}
.ew-level {
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}
.lv-immediate {
  background: #fdeaea;
  color: #c0392b;
}
.lv-priority {
  background: #fff6e9;
  color: #a05a00;
}
.lv-observe {
  background: #eef0f4;
  color: #6e6b7b;
}
.ew-msg {
  font-size: 12.5px;
  color: #5e5873;
}
.help-ic {
  width: 14px;
  height: 14px;
  cursor: pointer;
  color: #b9b8c5;
}

/* 정보 보완 안내 */
.info-box {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 8px;
  margin-top: 16px;
}
.info-box.warn {
  background: #fff6e9;
  border: 1px solid #ffd9a8;
}
.ib-ic {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #ff9f43;
  margin-top: 1px;
}
.ib-sub {
  font-size: 12px;
  color: #6e6b7b;
  margin-top: 4px;
}
.chip {
  display: inline-block;
  background: #fff;
  border: 1px solid #e0ddef;
  border-radius: 12px;
  padding: 1px 10px;
  margin: 2px 4px 2px 0;
  font-size: 12px;
}

/* 섹션 공통 */
.rs-section {
  margin-top: 22px;
}
.rs-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #5e5873;
}
.rs-note {
  font-size: 12px;
  font-weight: 400;
  color: #888;
}
.help-text {
  font-size: 12.5px;
  color: #6e6b7b;
  margin: 0 0 10px;
}

/* 영향 요인 */
.cause-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.cause-list li {
  padding: 8px 0;
  border-bottom: 1px dashed #ebe9f1;
}
.cause-list li:last-child {
  border-bottom: 0;
}
.cause-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.cause-desc {
  font-weight: 600;
  color: #5e5873;
}
.cause-pct {
  font-size: 12px;
  color: #6e6b7b;
}
.cause-bar {
  height: 7px;
  background: #eef0f4;
  border-radius: 4px;
  margin-top: 5px;
  overflow: hidden;
}
.cause-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #7367f0, #9c93ff);
  border-radius: 4px;
}
.cause-code {
  font-size: 10.5px;
  color: #b9b8c5;
}

/* 유의사항(한계) */
.limitations {
  margin-top: 22px;
  background: #fff8e1;
  border: 1px solid #ffe9a8;
  border-radius: 8px;
  padding: 12px 14px;
  color: #7a6500;
}
.lim-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  margin-bottom: 6px;
}
.lim-ic {
  width: 16px;
  height: 16px;
  color: #c79a00;
}
.lim-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12.5px;
}
.lim-list li {
  margin: 2px 0;
}
.lim-tech {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #e8d98a;
}
.lim-tech-title {
  font-size: 11px;
  color: #a08a3a;
  font-weight: 600;
  margin-bottom: 4px;
}
.lim-tech-list {
  margin: 0;
  padding-left: 18px;
  font-size: 11px;
  color: #a08a3a;
  list-style: disc;
}
.lim-tech-list li {
  margin: 1px 0;
  word-break: break-all;
}
.decision-cell {
  white-space: nowrap;
}
.rec-actions {
  display: flex;
  align-items: center;
}
.decision-badge {
  display: inline-block;
  margin-top: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.badge-accept {
  background: #e3f8ee;
  color: #28c76f;
}
.badge-edit {
  background: #eef0ff;
  color: #667eea;
}
.badge-reject {
  background: #fdeaea;
  color: #e42728;
}
.edit-box {
  margin-top: 8px;
  min-width: 220px;
}
.rec-row-rejected {
  opacity: 0.55;
}
.rec-row-rejected .rec-title,
.rec-row-rejected .rec-action {
  text-decoration: line-through;
}
.rec-table {
  width: 100%;
  border-collapse: collapse;
}
.rec-table th,
.rec-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}
.rec-table th {
  background: #fafafa;
}
.rec-title {
  font-weight: 600;
}
.rec-action {
  margin-top: 4px;
  color: #555;
  font-size: 13px;
}
.evidence-list {
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
  color: #555;
}
.empty-cell {
  text-align: center;
  color: #999;
}
</style>
